import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft, Mountain, Heart, Zap, ClipboardList, Sparkles, CalendarDays, Moon, Stars } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { WomensRetreatBookingModal } from '@/components/WomensRetreatBookingModal';
import { SEO } from '@/components/SEO';
import { StickyBookingButton } from '@/components/StickyBookingButton';
import { PageNavigation } from '@/components/PageNavigation';
import womensRetreatSunriseImage from '@/assets/womens-retreat-hiking.jpg';
const WomensRetreat = () => {
  const navigate = useNavigate();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const navigationSections = [{
    id: 'hero',
    label: 'Overview',
    icon: Moon
  }, {
    id: 'quick-info',
    label: 'Details',
    icon: ClipboardList
  }, {
    id: 'why',
    label: 'Why Join',
    icon: Sparkles
  }, {
    id: 'program',
    label: 'Program',
    icon: CalendarDays
  }, {
    id: 'benefits',
    label: 'Benefits',
    icon: Stars
  }];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleBooking = () => {
    setIsBookingModalOpen(true);
  };
  const handleGoBack = () => {
    navigate('/#experiences');
  };
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Circle in the Mountains - Women's Retreat",
    "description": "Reconnect with Yourself, Sisterhood, and Inner Flow in a transformative weekend retreat for women in the Polish mountains.",
    "startDate": "2025-11-21",
    "endDate": "2025-11-23",
    "eventStatus": "https://schema.org/EventSoldOut",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "Beskid Wyspowy",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Beskid Wyspowy",
        "addressCountry": "PL"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    }
  };
  const faqItems = [{
    question: "What should I bring to the women's retreat?",
    answer: "Bring comfortable clothing for yoga and movement, warm layers for mountain evenings, personal items, and an open heart. We encourage leaving your phone behind or keeping it on airplane mode for the full offline experience."
  }, {
    question: "Is this retreat suitable for all experience levels?",
    answer: "Yes! The retreat welcomes women of all fitness and experience levels. Yoga and movement sessions can be adapted to individual needs. What matters most is your openness to connection and presence."
  }, {
    question: "What's included in the retreat?",
    answer: "The retreat includes accommodation in a mountain shelter, all nourishing meals, guided yoga and movement sessions, women's circle workshops, breathwork, and the full weekend program. Transportation to the starting point is not included."
  }, {
    question: "How many women will be attending?",
    answer: "We keep the group intentionally small (12-16 women) to create an intimate, safe space for authentic connection and sharing."
  }];
  return <>
      <SEO title="Circle in the Mountains - Women's Retreat" description="Reconnect with Yourself, Sisterhood, and Inner Flow. A transformative weekend retreat for women in Beskid Wyspowy, Poland. Nov 21-23, 2025." canonicalUrl="https://mysticforest.club/experiences/womens-retreat" ogType="event" keywords={['womens retreat', 'sisterhood', 'wellness retreat', 'offline retreat', 'mountain retreat Poland', 'womens circle', 'yoga retreat', 'transformational retreat']} breadcrumbs={[{
      name: 'Experiences',
      url: '/#experiences'
    }, {
      name: "Women's Retreat",
      url: '/experiences/womens-retreat'
    }]} faq={faqItems} jsonLd={jsonLd} />
      
      <div className="min-h-screen bg-background">
      <PageNavigation sections={navigationSections} />
      <StickyBookingButton onBookNow={handleBooking} label="Join Waitlist" />
      <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button variant="ghost" onClick={handleGoBack} className="flex items-center gap-2" aria-label="Go back to experiences">
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section id="hero" className="relative py-32">
        <div className="absolute inset-0">
          <img src={womensRetreatSunriseImage} alt="Women hikers with backpacks walking through misty mountain valley at sunrise during women's retreat in Polish mountains, embodying sisterhood and connection with nature" className="w-full h-full object-cover object-bottom" />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-4xl">🌙</span>
            <h1 className="text-4xl md:text-6xl font-serif font-light">
              Circle in the Mountains
            </h1>
          </div>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Mystic Forest Women's Retreat – Reconnect with Yourself, Sisterhood, and Inner Flow
          </p>
          <div className="flex items-center justify-center gap-6 mb-8 text-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>November 21–23, 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span>Gorce, Poland</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-3 px-6 md:px-8 py-4 bg-muted/50 border border-white/30 rounded-md backdrop-blur-sm">
            <span className="text-base md:text-lg font-medium text-white">
              This retreat is sold out
            </span>
            <span className="text-2xl">🌙</span>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section id="quick-info" className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">November 21–23, 2025</p>
              <p className="text-sm text-muted-foreground">Weekend retreat</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Beskid Wyspowy</p>
              <p className="text-sm text-muted-foreground">Polish mountains</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Women seeking connection</p>
              <p className="text-sm text-muted-foreground">Authenticity & inner balance</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Days / 2 Nights</p>
              <p className="text-sm text-muted-foreground">Fully offline</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Why Section */}
          <div id="why" className="mb-16">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              🌙 Why?
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground text-center">
              <p className="text-xl leading-relaxed">
                A single flame glows, but it's the circle of lights that illuminates the night.
              </p>
              <p className="text-lg">
                In a world where women often give endlessly to others, we forget the power of pausing, receiving, and being together. 
                This retreat is a return to our natural rhythm: breath, presence, and the strength of sisterhood.
              </p>
            </div>
          </div>

          {/* Weekend Program */}
          <div id="program" className="mb-16">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              🗓️ Weekend Program
            </h2>
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 1 – Friday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Arrival and gentle walk into the mountains</li>
                    <li>• Silent walk – arriving in your body, sensing the landscape</li>
                    <li>• Opening women's circle by the fire in a mountain shelter</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 2 – Saturday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Morning ritual of yoga and breathwork</li>
                    <li>• Mountain hike – steps in silence, laughter, presence</li>
                    <li>• Workshop: "The Gift of Sisterhood" – exploring authenticity, self-care, and connection</li>
                    <li>• Evening fire – shared cooking, stories, songs, silence, togetherness</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 3 – Sunday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Closing circle – what do you carry back home?</li>
                    <li>• Descent from the mountains – embodying new clarity</li>
                    <li>• Integration: bringing sisterhood's energy into everyday life</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Benefits Grid */}
          <div id="benefits" className="grid md:grid-cols-3 gap-8 mb-16">
            <div>
              <h3 className="text-2xl font-serif font-light mb-6 flex items-center gap-2">
                ✨ Why Join?
              </h3>
              <ul className="space-y-4 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Sisterhood:</strong> reconnect with the strength of being together
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mountain className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Simplicity:</strong> mountains, fire, and honest presence – nothing more needed
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Offline:</strong> phones stay behind – you are fully here
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Transformation:</strong> you return nourished, centered, and renewed
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-serif font-light mb-6">
                📌 Who is it for?
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Women who long for pause, presence, and inner balance</li>
                <li>• Those seeking authentic connection in a safe circle</li>
                <li>• Women ready to nurture themselves as much as they nurture others</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-serif font-light mb-6">
                🏕️ What you'll gain
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• <strong className="text-foreground">Reset:</strong> clarity of mind, grounded body</li>
                <li>• <strong className="text-foreground">Sisterhood:</strong> support and inspiration from women like you</li>
                <li>• <strong className="text-foreground">Renewal:</strong> peace within, warmth around, strength inside</li>
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center bg-muted/50 rounded-2xl p-12">
            <h3 className="text-3xl font-serif font-light mb-6 text-foreground">
              👉 Step into the Circle
            </h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join Mystic Forest Women's Retreat and rediscover the quiet strength of sisterhood in the mountains.
              Limited spaces available for this intimate weekend.
            </p>
            <div className="inline-flex items-center gap-3 px-6 md:px-12 py-4 bg-muted/50 border border-muted-foreground/20 rounded-md">
              <span className="text-base md:text-lg font-medium text-muted-foreground">
                This retreat is sold out
              </span>
              <span className="text-2xl">🌙</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <WomensRetreatBookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
     </div>
     </>;
};
export default WomensRetreat;